import { getLateOrders, requestUnsyncedOrders } from "../api/orders";
import {
  lateOrdersToAskResponse,
  looksLikeLateQuestion,
  readLateOrdersDays,
} from "./ask";

const orderCustomer = (order = {}) =>
  order.customerName ||
  order.shippingAddress?.name ||
  order.billingAddress?.name ||
  order.customer?.name ||
  "customer";

const asList = (payload) => {
  if (Array.isArray(payload?.orders)) return payload.orders;
  if (Array.isArray(payload)) return payload;
  return [];
};

export const looksLikeUnsyncedQuestion = (value) =>
  /\b(unsync|listing sync|listings? (?:are )?not synced|need a listing)\b/i.test(
    String(value || "")
  );

export const looksLikeStoreTokenQuestion = (value) =>
  /\b(stop(?:ped)? syncing|token expired|reconnect)\b/i.test(String(value || ""));

export const looksLikeConnectStoreQuestion = (value) =>
  /\b(connect (?:a |my )?store|add (?:a |my )?shop|connect (?:shopify|etsy|woo))\b/i.test(
    String(value || "")
  );

export const looksLikeIntegrationsQuestion = (value) =>
  /\b(integrations?|shipping software|where do i connect)\b/i.test(
    String(value || "")
  );

export const looksLikeQcStationQuestion = (value) =>
  /\b(qc station|shipping toggle|qc vs shipping|qc and shipping|separate qc|pre-ship qc|workstation)\b/i.test(
    String(value || "")
  );

export const looksLikeHandoffQuestion = (value) =>
  /\b(talk to (a )?(human|someone|person)|speak to (a )?(human|someone|person)|chat to speak|real person|human please|contact support|get (me )?a (human|person)|need a (human|person)|customer support)\b/i.test(
    String(value || "")
  );

const HANDOFF_WRAPPER =
  /\b((talk|speak|chat) to (a )?(human|someone|person)|real person|human please|contact support|get (me )?a (human|person)|need a (human|person)|customer support|please|about|regarding)\b/gi;

export const hasHandoffTopic = (value) => {
  const rest = String(value || "")
    .replace(HANDOFF_WRAPPER, " ")
    .replace(/[?!.]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return rest.length >= 8;
};

const lastAssistantTurn = (transcript = []) =>
  [...transcript].reverse().find((row) => row.role === "assistant");

const isHandoffPromptTurn = (row) =>
  row?.kind === "handoff_prompt" ||
  /^What is this about\?/i.test(String(row?.content || row?.body || ""));

const handoffPromptAnswer = () => ({
  kind: "handoff_prompt",
  body: [
    "What is this about?",
    "A sentence is enough — the order, the store, or what is stuck. I’ll pass it to someone on the team.",
  ],
  steps: [{ src: "help", label: "Connecting you with the team" }],
});

const MANOWAR_BOT_ID = "sLb2cnOGy6AJTjVmZfDR";
const MANOWAR_HUB_ID = "KCAldrttjRdqN9LDLrl6";
const MANOWAR_SEND_URL = "https://manowar.cloud/api/widget/send";

const escalateToManowar = async ({
  question,
  user = {},
  pageContext = null,
  transcript = [],
} = {}) => {
  const visitorId = user.uid || user.id || `ask-${Date.now()}`;
  const who = [user.displayName || user.name, user.email]
    .filter(Boolean)
    .join(" · ");
  const turns = (Array.isArray(transcript) ? transcript : [])
    .slice(-12)
    .map((row) => {
      const role = row.role === "assistant" ? "Ask" : "User";
      return `${role}: ${String(row.content || row.text || "").slice(0, 800)}`;
    });
  const text = [
    "Human requested from Riverr Ask.",
    who ? `From: ${who}` : null,
    pageContext?.route
      ? `Page: ${pageContext.route}${
          pageContext.entity_id ? ` (${pageContext.entity_id})` : ""
        }`
      : null,
    question ? `They said: ${question}` : null,
    turns.length ? `Transcript:\n${turns.join("\n")}` : null,
  ]
    .filter(Boolean)
    .join("\n\n")
    .slice(0, 8000);

  const res = await fetch(MANOWAR_SEND_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      hubId: MANOWAR_HUB_ID,
      botId: MANOWAR_BOT_ID,
      visitorId: String(visitorId),
      text,
      invoke: false,
      requestHuman: true,
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !(data?.conversation?.id || data?.conversation_id || data?.id)) {
    throw new Error(data?.error || "manowar_send_failed");
  }
  return {
    kind: "handoff",
    body: [
      "Someone on the team has what you wrote and will follow up.",
    ],
    steps: [{ src: "help", label: "Connecting you with the team" }],
  };
};

const storeNameFromQuestion = (value) => {
  const match = String(value || "").match(/why did (.+?) stop syncing/i);
  return match?.[1]?.replace(/\?+$/, "").trim() || "This store";
};

const unsyncedOrdersToAskResponse = async () => {
  const res = await requestUnsyncedOrders({ status: "unsynced" });
  const orders = asList(res?.data).slice(0, 8);
  const rows = orders.map((order) => ({
    id: String(order.orderNumber || order.platformOrderId || order.id || "").slice(
      0,
      24
    ),
    label: orderCustomer(order),
    note: "unsynced listing",
    meta: "",
    flag: "red",
    href: order.id ? `/orders/${order.id}` : "/unsynced-orders",
  }));
  const n = rows.length;
  return {
    body:
      n === 0
        ? ["No open orders are waiting on a listing sync."]
        : [
            `${n} ${n === 1 ? "order is" : "orders are"} waiting on a listing sync before they can move.`,
            "Open one and use Sync Listing on each unsynced item.",
          ],
    steps: [{ src: "shop", label: "Orders waiting on a listing sync" }],
    rows: rows.length ? rows : undefined,
    rowsFrom: rows.length ? "Unsynced orders" : undefined,
    actions: [
      {
        label: "Open unsynced orders",
        kind: "navigate",
        target: "/unsynced-orders",
      },
    ],
  };
};

const lateOrdersAnswer = async () => {
  const days = readLateOrdersDays();
  const res = await getLateOrders({
    days,
    orderBy: "asc",
    limit: 10,
    includeStats: true,
  });
  return lateOrdersToAskResponse(res?.data || {}, days);
};

const storeTokenAnswer = (question) => {
  const store = storeNameFromQuestion(question);
  return {
    body: [
      `${store} stopped syncing because its Shopify login expired. New orders and tracking will not sync until you reconnect it.`,
      "We refresh the hourly access token automatically. The refresh token lasts 90 days. After about 90 days unused — or if the app is uninstalled or revoked — Shopify invalidates it and you have to reauthenticate.",
      "Open the store, press Reauthenticate, then wait for the next sync.",
    ],
    steps: [{ src: "shop", label: "Store connection" }],
    actions: [{ label: "Open stores", kind: "navigate", target: "/shops" }],
  };
};

const connectStoreAnswer = () => ({
  body: [
    "Connect a storefront from Connected stores — not from Settings → Integrations.",
    "Open Connected stores, add a shop, pick Shopify, Etsy, Woo, or another channel, and finish the connect flow.",
  ],
  steps: [{ src: "help", label: "Connect a store" }],
  docLink: { title: "Connect a store", url: "/add-shop" },
  actions: [{ label: "Open connected stores", kind: "navigate", target: "/shops" }],
});

const integrationsAnswer = () => ({
  body: [
    "Settings → Integrations is for shipping software, distributors, Stripe keys, and OrderDesk.",
    "Shopify, Etsy, and other storefronts are connected from Connected stores.",
  ],
  steps: [{ src: "help", label: "Where to connect integrations" }],
  docLink: { title: "Integrations", url: "/settings/integrations" },
  actions: [
    {
      label: "Open integrations",
      kind: "navigate",
      target: "/settings/integrations",
    },
  ],
});

const qcStationAnswer = () => ({
  body: [
    "QC Station and Shipping are two modes for the same barcode page — only when Settings → Shipping settings → Advanced has “Use separate QC and Shipping computers” turned on. That switch is off by default so most shops never see the header toggle.",
    "QC Station is the inspection table. Scan the item, pass or fail it, and put it on the rack Settings assigns. You can add racks with rules — singles, multi-item, still needs a side, or a specific placement like front, back, or neck. First matching rule wins.",
    "Premade / preprinted items are pulled and put in a ship bin on Pull and Distribute (SS-####). After QC, Sort bins only scans printed pieces into that same bin. The last print says take the bin to shipping. The label waits until pull has the premade pieces and QC has the prints.",
    "Shipping is the pack-out computer. Scan the same barcode: a one-of-one prints immediately. A finished multi-item bin asks you to confirm, then Enter (same as QC pass) prints.",
    "Set each computer once in the header. Do not flip the toggle per scan.",
  ],
  steps: [{ src: "help", label: "QC Station vs Shipping" }],
  docLink: {
    title: "QC and shipping stations",
    url: "/shipping-settings?tab=advanced",
  },
  actions: [
    {
      label: "Open shipping settings",
      kind: "navigate",
      target: "/shipping-settings?tab=advanced",
    },
  ],
});

export const resolveAskQuestion = async (
  question,
  { kind, user, pageContext, transcript } = {}
) => {
  const q = String(question || "").trim();
  if (!q) return null;
  if (isHandoffPromptTurn(lastAssistantTurn(transcript))) {
    return escalateToManowar({ question: q, user, pageContext, transcript });
  }
  if (kind === "handoff" || looksLikeHandoffQuestion(q)) {
    if (kind === "handoff" || !hasHandoffTopic(q)) {
      return handoffPromptAnswer();
    }
    return escalateToManowar({ question: q, user, pageContext, transcript });
  }
  if (kind === "late" || (!kind && looksLikeLateQuestion(q))) {
    return lateOrdersAnswer();
  }
  if (kind === "unsynced" || (!kind && looksLikeUnsyncedQuestion(q))) {
    return unsyncedOrdersToAskResponse();
  }
  if (kind === "store_token" || (!kind && looksLikeStoreTokenQuestion(q))) {
    return storeTokenAnswer(q);
  }
  if (kind === "howto_connect_store" || (!kind && looksLikeConnectStoreQuestion(q))) {
    return connectStoreAnswer();
  }
  if (kind === "howto_integrations" || (!kind && looksLikeIntegrationsQuestion(q))) {
    return integrationsAnswer();
  }
  if (kind === "howto_qc_station" || (!kind && looksLikeQcStationQuestion(q))) {
    return qcStationAnswer();
  }
  return null;
};

const Q = {
  late: {
    id: "late",
    kind: "late",
    tag: "your data",
    q: "Why are orders late?",
  },
  unsynced: {
    id: "unsynced",
    kind: "unsynced",
    tag: "your data",
    q: "Which orders still need a listing sync?",
  },
  connectStore: {
    id: "connect-store",
    kind: "howto_connect_store",
    tag: "how-to",
    q: "How do I connect a store?",
  },
  integrations: {
    id: "integrations",
    kind: "howto_integrations",
    tag: "how-to",
    q: "Where do I connect integrations?",
  },
  qcStation: {
    id: "qc-station",
    kind: "howto_qc_station",
    tag: "how-to",
    q: "What's the difference between QC Station and Shipping?",
  },
  handoff: {
    id: "handoff",
    kind: "handoff",
    tag: "help",
    q: "Talk to a person",
  },
};

const PAGE_SUGGESTIONS = {
  "/": [Q.late, Q.unsynced, Q.handoff],
  "/dashboard": [Q.late, Q.unsynced, Q.handoff],
  "/late-orders": [Q.late],
  "/unsynced-orders": [Q.unsynced],
  "/orders": [Q.late, Q.unsynced],
  "/draft-orders": [Q.late, Q.unsynced],
  "/shops": [Q.connectStore],
  "/add-shop": [Q.connectStore],
  "/integrations": [Q.integrations, Q.connectStore],
  "/settings": [Q.integrations, Q.connectStore],
  "/settings/integrations": [Q.integrations, Q.connectStore],
  "/settings/shipping/settings": [Q.qcStation],
  "/shipping-settings": [Q.qcStation],
  "/batch-item": [Q.qcStation],
};

export const suggestionsForPage = (route = "/") => {
  const path = String(route || "/").split("?")[0] || "/";
  if (PAGE_SUGGESTIONS[path]) return PAGE_SUGGESTIONS[path];
  if (path.startsWith("/batch-item")) return [Q.qcStation];
  if (path.startsWith("/settings/shipping") || path.startsWith("/shipping-settings"))
    return [Q.qcStation];
  if (path.startsWith("/settings")) return [Q.integrations, Q.connectStore];
  return [];
};
